import React, { useState, useEffect } from 'react';
import { BarChart3, FileText, TrendingDown, Activity, Download, RefreshCw } from 'lucide-react';
import { Card, Btn, Table, Spinner, ErrBox } from '../../../components/UI';
import { fmtRp, fmtDate } from '../../../utils/format';
import { assetsAPI } from '../../../utils/api';

const inputStyle = { padding: "9px 12px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13, outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box" };

export default function ReportsTab() {
  const [activeTab, setActiveTab] = useState('summary');
  const [summary, setSummary] = useState(null);
  const [depData, setDepData] = useState([]);
  const [assetData, setAssetData] = useState([]);
  const [condData, setCondData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [depLoading, setDepLoading] = useState(false);
  const [assetLoading, setAssetLoading] = useState(false);
  const [filters, setFilters] = useState({ tahun: new Date().getFullYear(), category_id: '', condition: '', search: '' });
  const [categories, setCategories] = useState([]);
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const r = await assetsAPI.getReportSummary();
      setSummary(r.data);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  const loadDep = async () => {
    setDepLoading(true);
    try {
      const qs = filters.tahun ? `?year=${filters.tahun}` : '';
      const r = await assetsAPI.getDepReport(qs);
      setDepData(r.data || []);
    } catch (e) { setErr(e.message); }
    finally { setDepLoading(false); }
  };

  const loadAssetReport = async () => {
    setAssetLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.tahun) params.set('tahun', filters.tahun);
      if (filters.category_id) params.set('category_id', filters.category_id);
      if (filters.condition) params.set('condition', filters.condition);
      if (filters.search) params.set('search', filters.search);
      const qs = params.toString() ? '?' + params.toString() : '';
      const r = await assetsAPI.getAssetReport(qs);
      setAssetData(r.data || []);
    } catch (e) { setErr(e.message); }
    finally { setAssetLoading(false); }
  };

  const loadCond = async () => {
    try {
      const r = await assetsAPI.getConditionReport();
      setCondData(r.data || []);
    } catch (e) { setErr(e.message); }
  };

  const loadCats = async () => {
    try {
      const r = await assetsAPI.getCategories();
      setCategories(r.data || []);
    } catch (e) {}
  };

  useEffect(() => { loadSummary(); loadCats(); }, []);
  useEffect(() => { if (activeTab === 'depresiasi') loadDep(); }, [activeTab, filters.tahun]);
  useEffect(() => { if (activeTab === 'daftar-aset') loadAssetReport(); }, [activeTab, filters.tahun, filters.category_id, filters.condition, filters.search]);
  useEffect(() => { if (activeTab === 'kondisi') loadCond(); }, [activeTab]);

  const tabs = [
    { key: 'summary', label: 'Rekapitulasi', icon: <BarChart3 size={14}/> },
    { key: 'depresiasi', label: 'Depresiasi', icon: <TrendingDown size={14}/> },
    { key: 'daftar-aset', label: 'Daftar Aset', icon: <FileText size={14}/> },
    { key: 'kondisi', label: 'Kondisi Aset', icon: <Activity size={14}/> },
  ];

  const condColors = { 'Baik': '#10B981', 'Kurang Baik': '#F59E0B', 'Rusak': '#EF4444', 'Dipinjamkan': '#8B5CF6' };

  return (
    <div>
      <Card>
        <div style={{ display: "flex", gap: 4, borderBottom: "1.5px solid #E2E8F0", paddingBottom: 0, marginBottom: 0 }}>
          {tabs.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
              padding: "8px 16px", border: "none", borderBottom: activeTab === t.key ? "2.5px solid #3B82F6" : "2.5px solid transparent",
              background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
              fontSize: 13, fontWeight: activeTab === t.key ? 600 : 400, color: activeTab === t.key ? "#3B82F6" : "#6B7280",
              fontFamily: "inherit", marginBottom: -1.5
            }}>
              {t.icon}{t.label}
            </button>
          ))}
        </div>
      </Card>

      {err && <div style={{marginBottom: 12}}><ErrBox msg={err} onRetry={loadSummary}/></div>}

      {activeTab === 'summary' && (
        <div>
          {loading ? <Spinner/> : summary && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 20 }}>
                <Card>
                  <p style={{ margin: "0 0 4px", fontSize: 12, color: "#6B7280", fontWeight: 600 }}>Total Aset</p>
                  <p style={{ margin: 0, fontSize: 28, fontWeight: 700, color: "#111827" }}>{summary.total.count}</p>
                  <p style={{ margin: "4px 0 0", fontSize: 12, color: "#047857" }}>{fmtRp(Number(summary.total.total_value))}</p>
                </Card>
                {summary.byCondition?.map(c => (
                  <Card key={c.condition}>
                    <p style={{ margin: "0 0 4px", fontSize: 12, color: "#6B7280", fontWeight: 600 }}>{c.condition}</p>
                    <p style={{ margin: 0, fontSize: 28, fontWeight: 700, color: condColors[c.condition] || "#111827" }}>{c.count}</p>
                  </Card>
                ))}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <Card>
                  <h4 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 700 }}>Per Kategori</h4>
                  {summary.byCategory?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {summary.byCategory.map((c, i) => (
                        <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#F9FAFB", borderRadius: 8 }}>
                          <span style={{ fontSize: 13, fontWeight: 500 }}>{c.category || 'Tanpa Kategori'}</span>
                          <div style={{ textAlign: "right" }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{c.count}</span>
                            <span style={{ fontSize: 11, color: "#6B7280", marginLeft: 8 }}>{fmtRp(Number(c.value))}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : <p style={{ fontSize: 13, color: "#9CA3AF" }}>Tidak ada data</p>}
                </Card>
                <Card>
                  <h4 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 700 }}>Terbaru Ditambahkan</h4>
                  {summary.recentAdditions?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {summary.recentAdditions.map((a, i) => (
                        <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 10px", background: "#F9FAFB", borderRadius: 6, fontSize: 12 }}>
                          <span><span style={{fontFamily:"monospace"}}>{a.code}</span> — {a.name}</span>
                          <span style={{ color: "#6B7280" }}>{fmtDate(a.acquisition_date)}</span>
                        </div>
                      ))}
                    </div>
                  ) : <p style={{ fontSize: 13, color: "#9CA3AF" }}>Tidak ada data</p>}
                </Card>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'depresiasi' && (
        <div>
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Laporan Depresiasi</h3>
              <div style={{ display: "flex", gap: 8 }}>
                <select value={filters.tahun} onChange={e => setFilters({...filters, tahun: Number(e.target.value)})} style={{padding:"7px 12px",border:"1.5px solid #E2E8F0",borderRadius:8,fontSize:13,outline:"none",background:"white"}}>
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <Btn icon={<RefreshCw size={14}/>} variant="secondary" onClick={loadDep}>Refresh</Btn>
              </div>
            </div>
            {depLoading ? <Spinner/> : (
              <Table pageSize={10} cols={[
                { key: "code", label: "Kode", nowrap: true, render: v => <span style={{fontFamily:"monospace",fontSize:12}}>{v}</span> },
                { key: "name", label: "Nama Aset" },
                { key: "category", label: "Kategori" },
                { key: "acquisition_value", label: "Nilai Perolehan", align: "right", render: v => <span style={{fontFamily:"monospace",fontSize:12}}>{fmtRp(Number(v))}</span> },
                { key: "total_depreciation", label: "Total Depresiasi", align: "right", render: v => <span style={{fontFamily:"monospace",fontSize:12,color:"#DC2626"}}>{fmtRp(Number(v)||0)}</span> },
                { key: "book_value", label: "Nilai Buku", align: "right", render: v => <span style={{fontFamily:"monospace",fontSize:12,color:"#047857"}}>{fmtRp(Number(v)||Number(depData.find(d=>d.code===v)?.acquisition_value)||0)}</span> },
              ]} data={depData} emptyMsg="Tidak ada data depresiasi"/>
            )}
          </Card>
        </div>
      )}

      {activeTab === 'daftar-aset' && (
        <div>
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Daftar Aset Lengkap ({assetData.length})</h3>
              <Btn icon={<Download size={14}/>} variant="secondary" onClick={loadAssetReport}>Export</Btn>
            </div>
            <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
              <input placeholder="Cari kode/nama..." value={filters.search} onChange={e => setFilters({...filters, search: e.target.value})} style={{...inputStyle, width: 200}}/>
              <select value={filters.tahun} onChange={e => setFilters({...filters, tahun: Number(e.target.value)})} style={{...inputStyle, width: 130}}>
                <option value="">Semua Tahun</option>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <select value={filters.condition} onChange={e => setFilters({...filters, condition: e.target.value})} style={{...inputStyle, width: 150}}>
                <option value="">Semua Kondisi</option>
                <option value="Baik">Baik</option>
                <option value="Kurang Baik">Kurang Baik</option>
                <option value="Rusak">Rusak</option>
              </select>
            </div>
            {assetLoading ? <Spinner/> : (
              <Table pageSize={10} cols={[
                { key: "code", label: "Kode", nowrap: true, render: v => <span style={{fontFamily:"monospace",fontSize:12}}>{v}</span> },
                { key: "name", label: "Nama Aset" },
                { key: "category_name", label: "Kategori" },
                { key: "condition", label: "Kondisi", render: v => (
                  <span style={{ padding:"3px 10px", borderRadius:9999, fontSize:11, fontWeight:600, color:"white", background: condColors[v] || '#6B7280' }}>{v}</span>
                )},
                { key: "status", label: "Status" },
                { key: "acquisition_value", label: "Nilai", align: "right", render: v => <span style={{fontFamily:"monospace",fontSize:12}}>{fmtRp(Number(v))}</span> },
                { key: "acquisition_date", label: "Tgl Perolehan", render: v => fmtDate(v) },
              ]} data={assetData} emptyMsg="Tidak ada aset"/>
            )}
          </Card>
        </div>
      )}

      {activeTab === 'kondisi' && (
        <div>
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Kondisi Aset</h3>
            </div>
            {condData.length > 0 ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16 }}>
                {condData.map((c, i) => (
                  <Card key={i} style={{ textAlign: "center", padding: "24px 16px" }}>
                    <div style={{ width: 56, height: 56, borderRadius: "50%", background: (condColors[c.condition] || '#6B7280') + '22', display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
                      <span style={{ fontSize: 22, fontWeight: 700, color: condColors[c.condition] || '#6B7280' }}>{c.count}</span>
                    </div>
                    <p style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700, color: "#111827" }}>{c.condition}</p>
                    <p style={{ margin: 0, fontSize: 13, color: "#6B7280" }}>{fmtRp(Number(c.total_value))}</p>
                  </Card>
                ))}
              </div>
            ) : <Spinner/>}
          </Card>
        </div>
      )}
    </div>
  );
}
